import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'

import { cn } from 'src/shared/utils/cn'

import ProfileTab from './profile-tab'

type ProfileTabBarItem = {
  label: string
  active: boolean
  onClick: () => void
}

type ProfileTabSection = {
  id: string
  label: string
  ref: RefObject<HTMLElement | null>
}

interface ProfileTabBarProps {
  items?: ProfileTabBarItem[]
  sections?: ProfileTabSection[]
  defaultActiveTab?: string
  tabScrollOffset?: number
  scrollContainer?: HTMLElement | null
  onActiveTabChange?: (tab: string) => void
  sticky?: boolean
  className?: string
}

const ProfileTabBar = ({
  items,
  sections,
  defaultActiveTab,
  tabScrollOffset,
  scrollContainer,
  onActiveTabChange,
  sticky = true,
  className,
}: ProfileTabBarProps) => {
  const hasManagedTabs = Boolean(sections?.length)
  const [activeTab, setActiveTab] = useState<string | null>(() => defaultActiveTab ?? sections?.[0]?.id ?? null)
  const resolvedScrollOffset = tabScrollOffset ?? 0
  const tabsContainerRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<Array<HTMLDivElement | null>>([])
  const [indicatorStyle, setIndicatorStyle] = useState<{ width: number; left: number } | null>(null)

  const getScrollTop = useCallback(() => {
    if (scrollContainer === undefined) {
      return window.scrollY
    }

    return scrollContainer?.scrollTop ?? null
  }, [scrollContainer])

  const scrollToTab = useCallback(
    (tabId: string) => {
      if (!sections?.length) {
        return
      }

      const section = sections.find((candidate) => candidate.id === tabId)
      if (!section?.ref.current) {
        return
      }

      const top = section.ref.current.offsetTop - resolvedScrollOffset

      if (scrollContainer === undefined) {
        window.scrollTo({ top, behavior: 'smooth' })
        return
      }

      if (scrollContainer) {
        scrollContainer.scrollTo({ top, behavior: 'smooth' })
      }
    },
    [resolvedScrollOffset, scrollContainer, sections],
  )

  const updateActiveTab = useCallback(() => {
    if (!sections?.length) {
      return
    }

    const scrollTop = getScrollTop()
    if (scrollTop === null) {
      return
    }

    const threshold = scrollTop + resolvedScrollOffset + 2
    let nextActive = defaultActiveTab ?? sections[0]?.id ?? null
    let maxOffset = Number.NEGATIVE_INFINITY

    sections.forEach((section) => {
      const offsetTop = section.ref.current?.offsetTop
      if (offsetTop === undefined || offsetTop === null) {
        return
      }

      if (offsetTop <= threshold && offsetTop > maxOffset) {
        maxOffset = offsetTop
        nextActive = section.id
      }
    })

    if (nextActive) {
      setActiveTab((current) => (current === nextActive ? current : nextActive))
    }
  }, [defaultActiveTab, getScrollTop, resolvedScrollOffset, sections])

  useEffect(() => {
    if (!hasManagedTabs) {
      return
    }

    const scrollTarget = scrollContainer === undefined ? window : scrollContainer
    if (!scrollTarget) {
      return
    }

    const handleScroll = () => updateActiveTab()
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })

    return () => scrollTarget.removeEventListener('scroll', handleScroll)
  }, [hasManagedTabs, scrollContainer, updateActiveTab])

  useEffect(() => {
    if (!hasManagedTabs || activeTab) {
      return
    }

    setActiveTab(defaultActiveTab ?? sections?.[0]?.id ?? null)
  }, [activeTab, defaultActiveTab, hasManagedTabs, sections])

  useEffect(() => {
    if (!activeTab) {
      return
    }

    onActiveTabChange?.(activeTab)
  }, [activeTab, onActiveTabChange])

  const itemsToRender = useMemo(() => {
    if (hasManagedTabs) {
      return (sections ?? []).map((section) => ({
        label: section.label,
        active: section.id === activeTab,
        onClick: () => scrollToTab(section.id),
      }))
    }

    return items ?? []
  }, [activeTab, hasManagedTabs, items, scrollToTab, sections])

  const activeIndex = useMemo(() => {
    if (!itemsToRender.length) {
      return null
    }

    const index = itemsToRender.findIndex((item) => item.active)
    return index === -1 ? null : index
  }, [itemsToRender])

  const updateIndicator = useCallback(() => {
    if (activeIndex === null) {
      setIndicatorStyle(null)
      return
    }

    const tabElement = tabRefs.current[activeIndex]
    if (!tabElement) {
      setIndicatorStyle(null)
      return
    }

    setIndicatorStyle({
      left: tabElement.offsetLeft,
      width: tabElement.offsetWidth,
    })
  }, [activeIndex])

  useLayoutEffect(() => {
    updateIndicator()
  }, [updateIndicator, itemsToRender.length])

  useEffect(() => {
    if (!tabsContainerRef.current || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(() => updateIndicator())
    observer.observe(tabsContainerRef.current)

    return () => observer.disconnect()
  }, [updateIndicator])

  return (
    <div
      className={cn(
        'mx-auto flex w-full justify-center bg-white px-5 md:px-0',
        sticky && 'sticky top-0 z-10',
        className,
      )}
    >
      <div
        ref={tabsContainerRef}
        className="relative flex w-full max-w-[1000px] flex-row items-start border-b-[1.5px] border-lightBorder"
      >
        {itemsToRender.map((item, index) => (
          <ProfileTab
            key={item.label}
            ref={(node) => {
              tabRefs.current[index] = node
            }}
            label={item.label}
            active={item.active}
            onClick={item.onClick}
          />
        ))}
        {indicatorStyle && (
          <div
            className="pointer-events-none absolute bottom-[-1.5px] left-0 h-[1.5px] rounded-full bg-primary transition-all duration-300 ease-out"
            style={{
              width: indicatorStyle.width,
              transform: `translateX(${indicatorStyle.left}px)`,
            }}
          />
        )}
      </div>
    </div>
  )
}

export default ProfileTabBar
